import { FC, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Button,
  IconButton,
} from '@mui/material';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import useSWR from 'swr';
import { useHistory } from 'react-router-dom';
import { useAbility } from '@casl/react';
import palette from '../../theme/palette';
import { BlogPost } from '../../types/BlogPost';
import AddOrEditBlogPost from './AddOrEditBlogPost';
import BlogPostCard from './BlogPostCard';
import { AbilityContext } from '../../context/AbilityContext';
import ScrollToTop from '../utils/ScrollToTop';

export const Blog: FC = () => {
  const [openAddOrEditDialog, setOpenAddOrEditDialog] = useState(false);
  const [updatedBlogPost, setUpdatedBlogPost] = useState<BlogPost>({
    title: '',
    date: new Date(),
    post: '',
    image: '',
  });
  const ability = useAbility(AbilityContext);
  const history = useHistory();

  const { data: blogPosts } = useSWR<BlogPost[]>('/blogPosts', { suspense: true });

  return (
    <>
      <ScrollToTop />
      <Grid container>
        {ability.can('create', 'All') && (
          <Grid
            item
            container
            sx={{
              mt: 5,
              px: { xs: '16px', md: '50px' },
              display: 'flex',
              justifyContent: 'end',
            }}
          >
            <Button
              style={{ backgroundColor: palette.button.primary, color: palette.white }}
              onClick={() => {
                setUpdatedBlogPost({
                  title: '',
                  date: new Date(),
                  post: '',
                  image: '',
                });
                setOpenAddOrEditDialog(true);
              }}
            >
              Add Blog Post
            </Button>
          </Grid>
        )}
        <Grid
          item
          container
          sx={{
            marginTop: '70px',
            maxWidth: '80vw',
            display: 'flex',
            justifyContent: 'center',
            mx: 'auto',
            padding: { xs: '0 16px', md: '0 50px' },
          }}
        >
          {blogPosts && blogPosts.length && blogPosts[0] ? (
            <Grid item>
              {(ability.can('create', 'All') || ability.can('update', 'All')) && (
                <IconButton
                  style={{
                    backgroundColor: palette.button.primary,
                    color: palette.white,
                    position: 'absolute',
                  }}
                  onClick={() => {
                    setUpdatedBlogPost(blogPosts[0]);
                    setOpenAddOrEditDialog(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
              )}
              <Card sx={{ boxShadow: 'none', width: '100%', borderRadius: 0, mb: 10 }}>
                {blogPosts[0].image ? (
                  <CardMedia
                    component="img"
                    image={blogPosts[0].image}
                    alt={blogPosts[0].title}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '50vh',
                      objectFit: 'contain',
                      borderRadius: '4px',
                    }}
                  />
                ) : (
                  <Box
                    style={{
                      width: '100%',
                      height: '250px',
                      backgroundColor: palette.disabled,
                      color: palette.white,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '3px',
                    }}
                  >
                    <ImageNotSupportedIcon style={{ fontSize: '4rem' }} /> <span>No Photo</span>
                  </Box>
                )}
                <CardContent sx={{ width: '100%', padding: { xs: '16px 0', md: '20px 0' } }}>
                  <Typography
                    variant="h5"
                    sx={{
                      width: '100%',
                      borderBottom: `5px solid ${palette.paper.secondary}`,
                      paddingBottom: { xs: '16px', md: '20px' },
                      fontWeight: 'bold',
                      marginBottom: '20px',
                    }}
                  >
                    {blogPosts[0].title}
                  </Typography>
                  <Typography>{`${blogPosts[0].post.substring(0, 450)}...`}</Typography>
                  <Typography
                    variant="subtitle2"
                    style={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      marginTop: '15px',
                      textDecoration: 'underline',
                    }}
                    onClick={() => history.push(`/blog/${blogPosts[0].id}`)}
                  >
                    Read More
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            <Typography variant="h5">No Blog Posts</Typography>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        container
        sx={{
          paddingBottom: { xs: '16px', md: '50px' },
          display: 'flex',
          maxWidth: '80vw',
          mx: 'auto',
        }}
      >
        {blogPosts &&
          blogPosts.map((blogPost, blogPostIndex) => {
            if (blogPostIndex === 0) return;
            return <BlogPostCard key={blogPost.id} blogPost={blogPost} index={blogPostIndex} />;
          })}
      </Grid>
      {(ability.can('create', 'All') || ability.can('update', 'All')) && (
        <AddOrEditBlogPost
          open={openAddOrEditDialog}
          close={() => setOpenAddOrEditDialog(false)}
          blogPost={updatedBlogPost}
          updatedBlogPost={updatedBlogPost}
          setUpdatedBlogPost={setUpdatedBlogPost}
        />
      )}
    </>
  );
};

export default Blog;
